import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  styled,
  alpha,
  Divider,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  useTheme,
} from '@mui/material';
import {
  Lock,
  Email,
  Visibility,
  VisibilityOff,
  Person,
  Business,
  Phone,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { SignUpRequest } from '../types';
import { formatPhoneNumber, cleanPhoneNumber, phoneMaskProps } from '../utils/phoneMask';
import { ResetPasswordModal } from '../components/ResetPasswordModal';
import SignUpFlowPage from './SignUpFlowPage';
import { getLogoByDomain, LogoByDomainResponse } from '../utils/api';
import { useTranslation } from 'react-i18next';

const BackgroundContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: theme.palette.mode === 'dark'
    ? '#313131'
    : `
      radial-gradient(circle at 20% 80%, rgba(102, 126, 234, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(118, 75, 162, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(102, 126, 234, 0.05) 0%, transparent 50%),
      linear-gradient(135deg, #FAFBFC 0%, #F4F6F8 100%)
    `,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    width: '200%',
    height: '200%',
    background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" xmlns="http://www.w3.org/2000/svg"%3E%3Cdefs%3E%3Cpattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse"%3E%3Cpath d="M 60 0 L 0 0 0 60" fill="none" stroke="rgba(102,126,234,0.03)" stroke-width="1"/%3E%3C/pattern%3E%3C/defs%3E%3Crect width="100%25" height="100%25" fill="url(%23grid)"/%3E%3C/svg%3E")',
    animation: 'float 20s ease-in-out infinite',
    opacity: theme.palette.mode === 'dark' ? 0.25 : 0.5,
  },
}));

const LoginCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  width: '100%',
  maxWidth: 480,
  background: theme.palette.mode === 'dark' ? '#1a1a1a' : 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  borderRadius: 24,
  border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: theme.palette.mode === 'dark' ? '0 20px 60px rgba(0, 0, 0, 0.4)' : '0 20px 60px rgba(0, 0, 0, 0.08)',
  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: 'linear-gradient(90deg, #1a1a1a 0%, #1a1a1a 100%)',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: theme.palette.mode === 'dark' ? '#313131' : alpha(theme.palette.primary.main, 0.02),
    transition: 'all 0.3s ease',

    '& fieldset': {
      borderColor: theme.palette.mode === 'dark' ? '#313131' : alpha(theme.palette.primary.main, 0.1),
      borderWidth: 2,
    },

    '&:hover fieldset': {
      borderColor: theme.palette.mode === 'dark' ? '#313131' : alpha(theme.palette.primary.main, 0.3),
    },

    '&.Mui-focused': {
      backgroundColor: theme.palette.mode === 'dark' ? '#313131' : '#FFFFFF',

      '& fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
  },

  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,

    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },

  '& .MuiInputAdornment-root': {
    color: theme.palette.mode === 'dark' ? alpha('#E5E7EB', 0.7) : alpha(theme.palette.primary.main, 0.6),
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: '14px 32px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  color: '#1a1a1a',
  background: 'linear-gradient(135deg, #00f5ff 0%, #35e87c 100%)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    opacity: 0.8,
    transform: 'translateY(-2px)',
  },
  '&:disabled': {
    background: theme.palette.action.disabledBackground,
    boxShadow: 'none',
  },
}));

const UserTypeCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  background: alpha(theme.palette.primary.main, 0.03),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease',

  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.05),
    borderColor: alpha(theme.palette.primary.main, 0.2),
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: theme.spacing(4),

  '& img': {
    transition: 'transform 0.3s ease',

    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
}));

const LogoImage = styled('img')(({ theme }) => ({
  width: '240px',
  height: 'auto',
  filter: theme.palette.mode === 'dark'
    ? 'invert(1) brightness(1.2) drop-shadow(0 4px 12px rgba(0,0,0,0.3))'
    : 'brightness(0.2) drop-shadow(0 4px 12px rgba(0,0,0,0.1))',
}));

const LoginPage: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSignUpFlow, setShowSignUpFlow] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [dynamicLogo, setDynamicLogo] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(false);

  const { login, signUp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isAllowedDomain = () => {
    const currentDomain = window.location.origin;
    return currentDomain === 'http://localhost:3000' || currentDomain === 'https://platform.liatalk.ai';
  };

  // Função para extrair domínio da URL atual
  const getCurrentDomain = () => {
    const currentUrl = window.location.hostname;
    return currentUrl;
  };

  // Verificar se o usuário veio da ativação
  React.useEffect(() => {
    if (location.state?.message) {
      setError(location.state.message);
    }
  }, [location.state]);

  // Verificar se deve mostrar o fluxo de cadastro
  React.useEffect(() => {
    if (location.state?.showSignUp) {
      setShowSignUpFlow(true);
    }
  }, [location.state]);

  // Verificar hash na URL para mostrar o fluxo de cadastro
  React.useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#signup') {
      setShowSignUpFlow(true);
    }
  }, []);

  React.useEffect(() => {
    if (!isAllowedDomain() && activeTab === 1) {
      setActiveTab(0);
    }
  }, [activeTab]);

  // Carregar logo dinamicamente baseado no domínio
  React.useEffect(() => {
    const loadDynamicLogo = async () => {
      const domain = getCurrentDomain();

      // Só tenta carregar logo se não for localhost:3000 ou platform.liatalk.ai
      if (domain !== 'localhost' && domain !== 'platform.liatalk.ai') {
        setLogoLoading(true);
        try {
          const logoData = await getLogoByDomain(domain);
          if (logoData?.logoUrl) {
            setDynamicLogo(logoData.logoUrl);
          }
        } catch (error) {
          console.error('Error loading dynamic logo:', error);
        } finally {
          setLogoLoading(false);
        }
      }
    };

    loadDynamicLogo();
  }, []);

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const success = await login(email, password);
      if (success) {
        // Verificar se o usuário precisa selecionar um plano
        const needsPlanSelection = localStorage.getItem('needsPlanSelection');
        if (needsPlanSelection === 'true') {
          // Redirecionar para seleção de plano
          navigate('/change-plan', { state: { forceSelection: true } });
          return;
        }
        
        // Se o usuário veio da ativação, marcar como novo usuário para ir para welcome
        if (location.state?.message && location.state.message.includes('Conta ativada com sucesso')) {
          localStorage.setItem('isNewUser', 'true');
        }
        navigate('/', { state: { showWelcome: true } });
      } else {
        setError(t('auth.invalidCredentials'));
      }
    } catch (err: any) {
      if (err.message && err.message.includes('Email não verificado')) {
        setError(t('auth.emailNotVerified'));
      } else {
        setError(t('auth.loginError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUpClick = () => {
    setShowSignUpFlow(true);
    // Atualizar a URL com o hash #signup
    window.history.replaceState(null, '', '/login#signup');
  };

  const handleBackFromSignUp = () => {
    setShowSignUpFlow(false);
    // Remover o hash da URL ao voltar
    window.history.replaceState(null, '', '/login');
  };

  const handleForgotPasswordClick = () => {
    setResetPasswordOpen(true);
  };



  if (showSignUpFlow) {
    return <SignUpFlowPage onBack={handleBackFromSignUp} initialFormData={location.state?.formData} />;
  }

  return (
    <BackgroundContainer>
      <Container component="main" maxWidth="sm">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {(isAllowedDomain() || dynamicLogo) && (
            <LogoContainer>
              <Box sx={{ mb: 0 }}>
                {logoLoading ? (
                  <CircularProgress size={40} />
                ) : (
                  <LogoImage
                    src={dynamicLogo || "/logo-preto.svg"}
                    alt="Logo"
                    onError={() => setDynamicLogo(null)} // Fallback para logo padrão em caso de erro
                  />
                )}
              </Box>
            </LogoContainer>
          )}

          <LoginCard elevation={0}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  background: theme.palette.mode === 'dark'
                    ? '#00f5ff'
                    : 'linear-gradient(135deg, #1a1a1a 0%,rgb(75, 106, 137) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  mb: 1,
                }}
              >
                {t('auth.loginTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {activeTab === 0 ? t('auth.loginSubtitle') : t('auth.signUpSubtitle')}
              </Typography>
            </Box>

            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              centered
              sx={{
                mb: 3,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                },
                '& .Mui-selected': {
                  color: theme.palette.mode === 'dark' ? '#00f5ff !important' : '#1a1a1a !important',
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: theme.palette.mode === 'dark' ? '#00f5ff' : '#1a1a1a',
                },
              }}
            >
              <Tab label={t('common.login')} />
              {/* {isAllowedDomain() && <Tab label={t('common.signUp')} />} */}
            </Tabs>

            {error && (
              <Alert
                severity={error.includes('Conta ativada com sucesso') ? 'success' : 'error'}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  '& .MuiAlert-icon': {
                    color: error.includes('Conta ativada com sucesso') ? '#4caf50' : '#c62828',
                  },
                }}
              >
                {error}
              </Alert>
            )}

            {activeTab === 0 ? (
              <Box component="form" onSubmit={handleLoginSubmit}>
                <StyledTextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label={t('common.email')}
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <Email sx={{ mr: 1, color: 'action.active' }} />
                    ),
                  }}
                />

                <StyledTextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label={t('common.password')}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <Lock sx={{ mr: 1, color: 'action.active' }} />
                    ),
                    endAdornment: (
                      <Box
                        component="span"
                        onClick={() => setShowPassword(!showPassword)}
                        sx={{
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          color: 'action.active',
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </Box>
                    ),
                  }}
                />

                <SubmitButton
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{ mt: 4, mb: 2 }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: 'white' }} />
                  ) : (
                    t('common.login')
                  )}
                </SubmitButton>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 3 }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleForgotPasswordClick}
                    sx={{
                      textTransform: 'none',
                      color: theme.palette.mode === 'dark' ? '#00f5ff' : '#1a1a1a',
                      fontSize: '0.875rem',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(96, 165, 250, 0.04)' : 'rgba(44, 62, 80, 0.04)',
                      },
                    }}
                  >
                    {t('common.forgotPassword')}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" gutterBottom>
                  {t('auth.createAccountTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                  {t('auth.createAccountSubtitle')}
                </Typography>
                <SubmitButton
                  variant="contained"
                  size="large"
                  onClick={handleSignUpClick}
                  sx={{ minWidth: 200 }}
                >
                  {t('auth.startSignUp')}
                </SubmitButton>
              </Box>
            )}
          </LoginCard>

          {/* Modal de Reset de Senha */}
          <ResetPasswordModal
            open={resetPasswordOpen}
            onClose={() => setResetPasswordOpen(false)}
          />
        </Box>
      </Container>
    </BackgroundContainer>
  );
};

export default LoginPage;
